import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Lightbulb, Save, Trash2, Wand2, BookHeart } from "lucide-react";
import { toast } from "sonner";
import { EMPTY_PROFILE } from "@/hooks/useTeacherProfile";
import { SUBJECTS } from "@/data/tags";
import { X, Tags } from "lucide-react";

const CustomTagsList = ({ customTags, onChange }) => {
  const entries = Object.entries(customTags).filter(([, tags]) => tags?.length);
  const remove = (slug, tag) =>
    onChange({ ...customTags, [slug]: customTags[slug].filter((t) => t !== tag) });
  return (
    <div className="space-y-3" data-testid="profile-custom-tags">
      <div>
        <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          <Tags className="h-3.5 w-3.5 text-slate-500" />
          Vaše vlastite oznake
        </Label>
        <p className="text-xs text-slate-500 mt-0.5">
          Dodajete ih gumbom „Dodaj vlastitu oznaku” ispod oznaka predmeta. Vrijede za
          taj predmet u svim razredima.
        </p>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-400 italic" data-testid="profile-custom-tags-empty">
          Još niste dodali vlastite oznake.
        </p>
      ) : (
        entries.map(([slug, tags]) => (
          <div key={slug} className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {SUBJECTS.find((s) => s.value === slug)?.label || slug}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-2.5 pr-1 py-0.5 text-xs text-slate-700"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => remove(slug, tag)}
                    aria-label="Ukloni"
                    data-testid={`profile-custom-tag-remove-${slug}-${tags.indexOf(tag)}`}
                    className="h-4 w-4 grid place-items-center rounded-full text-slate-400 hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const ToneExample = ({ tone }) => {
  const examples = {
    topao: `„Marta s velikim entuzijazmom otkriva nove matematičke sadržaje i raduje se svakom novom izazovu."`,
    neutralan: `„Učenica aktivno sudjeluje u nastavi matematike i pokazuje vidljiv napredak u rješavanju zadataka."`,
    formalan: `„Učenica zadovoljava ishode učenja te samostalno primjenjuje usvojene matematičke sadržaje."`,
  };
  return (
    <div className="text-sm text-slate-600 italic leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3">
      {examples[tone] || examples.neutralan}
    </div>
  );
};

export default function TeacherProfileSheet({
  open,
  onOpenChange,
  initialProfile,
  onSave,
  onReset,
}) {
  const [draft, setDraft] = useState(initialProfile || EMPTY_PROFILE);

  useEffect(() => {
    if (open) setDraft(initialProfile || EMPTY_PROFILE);
  }, [open, initialProfile]);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handleSave = () => {
    onSave(draft);
    toast.success("Profil je spremljen. AI će ga koristiti pri sljedećem generiranju.");
    onOpenChange(false);
  };

  const handleReset = () => {
    onReset();
    setDraft((d) => ({ ...EMPTY_PROFILE, custom_tags: d.custom_tags || {} }));
    toast.success("Profil je obrisan. Vaše vlastite oznake su zadržane.");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto bg-white"
        data-testid="teacher-profile-sheet"
      >
        <SheetHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-blue-50 border border-blue-100 grid place-items-center">
              <BookHeart className="h-4.5 w-4.5 text-blue-600" />
            </div>
            <SheetTitle className="text-xl font-heading">Profil učitelja</SheetTitle>
          </div>
          <SheetDescription className="text-slate-600 leading-relaxed">
            {`Vaš osobni „recept" po kojem AI piše tekstove. Postavite ga jednom i svaki sljedeći generirani tekst zvučat će više kao vi.`}
          </SheetDescription>
        </SheetHeader>

        {/* Explainer card */}
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <div className="flex items-start gap-2.5">
            <Lightbulb className="h-4.5 w-4.5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
              <p className="font-semibold text-slate-900">Što je ovo i čemu služi?</p>
              <p>
                AI ne zna kako vi pišete praćenja. Bez ovog profila daje „prosječan"
                tekst koji je u redu, ali ne zvuči kao vi. Ovdje mu kažete{" "}
                <strong>kako želite da vam piše</strong> – ton, dužinu, vaše izraze.
              </p>
              <p>
                {`Zamislite to ovako: kao da novom kolegi koji vas mijenja date kratku uputu „ja obično pišem ovako i nikad ne koristim tu riječ".`}
              </p>
              <p className="text-slate-600">
                Sve se sprema lokalno u vašem pregledniku – ne šaljemo to nikome i
                ostaje samo na ovom računalu.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {/* Signature */}
          <div className="space-y-2">
            <Label htmlFor="signature" className="text-sm font-medium text-slate-700">
              Vaše ime ili potpis{" "}
              <span className="text-slate-400 font-normal">(opcionalno)</span>
            </Label>
            <Input
              id="signature"
              placeholder="npr. učiteljica Marija Horvat"
              value={draft.signature_name}
              onChange={(e) => update({ signature_name: e.target.value })}
              data-testid="profile-signature-input"
              className="rounded-lg"
            />
            <p className="text-xs text-slate-500">
              Ne pojavljuje se u tekstu, samo pomaže AI-ju da pogodi ton (npr.
              razlika između razredne i predmetne nastavnice).
            </p>
          </div>

          <Separator />

          {/* Tone */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Ton pisanja
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Kako želite da tekst „zvuči"?
              </p>
            </div>
            <RadioGroup
              value={draft.tone}
              onValueChange={(v) => update({ tone: v })}
              className="grid gap-2"
              data-testid="profile-tone-group"
            >
              {[
                ["topao", "Topao i osobniji", `Više emocije, ime učenika, „raduje se", „otkriva".`],
                ["neutralan", "Neutralan i profesionalan", "Standardan ton e-Dnevnika. Sigurna sredina."],
                ["formalan", "Formalan i sažet", "Strogo stručni rječnik, kratke rečenice, manje emocije."],
              ].map(([val, label, hint]) => (
                <label
                  key={val}
                  htmlFor={`tone-${val}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    draft.tone === val
                      ? "border-blue-500 bg-blue-50/60"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <RadioGroupItem
                    value={val}
                    id={`tone-${val}`}
                    className="mt-0.5"
                    data-testid={`profile-tone-${val}`}
                  />
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium text-slate-800">{label}</div>
                    <div className="text-xs text-slate-500 leading-snug">{hint}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
            <div className="mt-2">
              <p className="text-xs text-slate-500 mb-1.5">Kako bi to zvučalo:</p>
              <ToneExample tone={draft.tone} />
            </div>
          </div>

          <Separator />

          {/* Length */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Duljina teksta
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Koliko detaljno želite da AI piše?
              </p>
            </div>
            <RadioGroup
              value={draft.length}
              onValueChange={(v) => update({ length: v })}
              className="grid grid-cols-1 gap-2"
              data-testid="profile-length-group"
            >
              {[
                ["kratak", "Kratak", "2–3 rečenice — za brzu povratnu informaciju."],
                ["srednji", "Srednji", "4–6 rečenica — preporučeno za e-Dnevnik."],
                ["dugi", "Detaljan", "6–8 rečenica — za polugodišnja i godišnja praćenja."],
              ].map(([val, label, hint]) => (
                <label
                  key={val}
                  htmlFor={`length-${val}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    draft.length === val
                      ? "border-blue-500 bg-blue-50/60"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <RadioGroupItem
                    value={val}
                    id={`length-${val}`}
                    className="mt-0.5"
                    data-testid={`profile-length-${val}`}
                  />
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium text-slate-800">{label}</div>
                    <div className="text-xs text-slate-500 leading-snug">{hint}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Custom notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-slate-700">
              Vaše osobne upute za AI{" "}
              <span className="text-slate-400 font-normal">(opcionalno)</span>
            </Label>
            <Textarea
              id="notes"
              placeholder={`Primjeri:
• Nikad ne koristi riječ „dijete", uvijek „učenik".
• Često koristim izraz „uz dodatnu podršku".
• Izbjegavaj uskličnike.
• Ja sam vjeroučiteljica pa ton neka bude topao i ohrabrujući.`}
              value={draft.custom_notes}
              onChange={(e) => update({ custom_notes: e.target.value })}
              data-testid="profile-notes-textarea"
              className="min-h-[140px] rounded-lg text-sm leading-relaxed"
            />
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
              <div className="flex items-center gap-1.5 mb-1 font-medium text-slate-800">
                <Wand2 className="h-3.5 w-3.5" />
                Savjet
              </div>
              Pišite jednostavno, kao da objašnjavate kolegi. Možete dodati riječi koje
              često koristite, riječi koje izbjegavate, ili koji ste razrednik. AI će
              to uvažiti kod svakog generiranja.
            </div>
          </div>

          <Separator />

          <CustomTagsList
            customTags={draft.custom_tags || {}}
            onChange={(custom_tags) => update({ custom_tags })}
          />
        </div>

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-8 px-6 pt-4 pb-5 bg-white border-t border-slate-200 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={handleReset}
            data-testid="profile-reset-btn"
            className="text-slate-500 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Obriši profil
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="profile-cancel-btn"
            >
              Odustani
            </Button>
            <Button
              onClick={handleSave}
              data-testid="profile-save-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="h-4 w-4 mr-1.5" />
              Spremi profil
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
